import Axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { useMemo } from 'react';
import { useSession } from '@clerk/clerk-react';

const TOKEN_RETRY_DELAY_MS = 1000;
const TOKEN_RETRY_MAX_ATTEMPTS = 3;

function wait(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
}

export const useAuthenticatedAxiosInstance = (baseURL: string): AxiosInstance => {
    const { session } = useSession();

    const api = useMemo<AxiosInstance>(() => {
        const api = Axios.create({ baseURL });

        api.interceptors.request.use(
            async function (config) {
                const sessionToken = await session?.getToken();
                if (sessionToken) {
                    config.headers = {
                        ...config.headers,
                        authorization: `Bearer ${sessionToken}`,
                    };
                }
                return config;
            },
            function (error) {
                return Promise.reject(error);
            },
        );

        api.interceptors.response.use(
            response => response,
            async function (error) {
                const originalRequest = error.config as AxiosRequestConfig & { _authRetryCount?: number };
                if (error.response?.status !== 401 || !originalRequest) {
                    return Promise.reject(error);
                }

                const retryCount = originalRequest._authRetryCount ?? 0;
                if (retryCount >= TOKEN_RETRY_MAX_ATTEMPTS) {
                    return Promise.reject(error);
                }

                originalRequest._authRetryCount = retryCount + 1;

                // Wait for Clerk to finish refreshing the session token
                await wait(TOKEN_RETRY_DELAY_MS);

                const freshToken = await session?.getToken();
                if (!freshToken) {
                    return Promise.reject(error);
                }

                if (!originalRequest.headers) {
                    originalRequest.headers = {};
                }
                originalRequest.headers.authorization = `Bearer ${freshToken}`;
                return api.request(originalRequest);
            },
        );

        return api;
    }, [baseURL, session]);

    return api;
};
